import { Controller } from '@hotwired/stimulus'
import { useDebounce } from 'stimulus-use'

// Connects to data-controller="form"
export default class extends Controller {
  static targets = ['onlyValidateField', 'submitButton']
  static values = {
    modelChanged: Boolean
  }

  static debounces = [
    {
      name: 'validate',
      wait: 500
    }
  ]

  connect() {
    this.isSubmitting = false
    useDebounce(this)
    this.removeUnloadEvent()

    // Listen for turbo:before-stream-render to prevent validation responses after submission
    this.boundHandleBeforeStreamRender = this.handleBeforeStreamRender.bind(this)
    document.addEventListener('turbo:before-stream-render', this.boundHandleBeforeStreamRender)
  }

  disconnect() {
    document.removeEventListener('turbo:before-stream-render', this.boundHandleBeforeStreamRender)
  }

  handleBeforeStreamRender(event) {
    // Prevent validation-only turbo streams from rendering if we're already submitting
    if (this.isSubmitting) {
      // Check if this is a validation-only response
      const streamElement = event.detail.newStream
      if (streamElement && streamElement.dataset.validationOnly === 'true') {
        event.preventDefault()
      } else {
        // This is a form submission error response - reset isSubmitting so validation can work again
        this.isSubmitting = false
      }
    }
  }

  handleKeydown(event) {
    // Handle Enter key press to submit form properly
    if (event.key === 'Enter') {
      this.submit(event)
    }
  }

  modelChangedValueChanged() {
    if (this.modelChangedValue) {
      this.addUnloadEvent() // Notify the user that changes have been made using a browser message
    } else {
      this.removeUnloadEvent()
    }
  }

  addUnloadEvent() {
    window.onbeforeunload = (event) => {
      event.preventDefault()
      event.returnValue = '' // required by Chrome
    }
  }

  removeUnloadEvent() {
    window.onbeforeunload = null
  }

  submit(event = null) {
    if (event && this.hasSubmitButtonTarget) {
      event.preventDefault()

      // check HTML5 form validations and tell submit button to start loading if they pass
      if (this.element.checkValidity()) {
        this.submitButtonTarget.dispatchEvent(new Event('formSubmitting'))
      } else {
        this.element.reportValidity() // show native validation messages
        return
      }
    }

    // Mark that we're submitting to prevent validation interference
    this.isSubmitting = true
    this.element.requestSubmit()
  }

  validate() {
    // this function is debounced via the "debounces" list above
    this.submitWithOnlyValidate()
  }

  fastValidate() {
    // this function isn't debounced
    this.submitWithOnlyValidate()
  }

  submitWithOnlyValidate() {
    // Submit the form for validations and no persistence

    // Don't validate if we're already submitting the form
    if (this.isSubmitting) {
      return
    }

    // 1. Set the hidden form field "only_validate" to true (only_validate=true in the Rails controller)
    this.onlyValidateFieldTarget.value = true

    // 2. Submit the form
    this.element.requestSubmit()
  }
}
